import { SelectedFilters } from "@/routes/Frontend/FuturebilityCockpit/hooks";
import { DtoCubeDataResponse, GenericObject } from "@/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReflectionType } from "../../../../../../types/domain-types";
import { BarChart } from "../../../components/BarChart";
import { CATEGORY_ICONS } from "../../../components/ChallengeRow";
import { DoubleToggle } from "../../../components/DoubleToggle";
import { MenuButton } from "../../../components/MenuButton";
import { Cube } from "../../../components/Сube";
import { CubeSideName } from "../../../components/Сube/types";
import { useReflectionCategoryPageData } from "../../../hooks/useReflectionCategoryPageData/useReflectionCategoryPageData";
import { AiResponse, Category, CategoryNavItem } from "../../../types";
import {
  calculatePercentagesForNumbers,
  formatAiHtmlString,
  getAnswerSuggestionForReflectionModule,
  renderNumber,
  translateErrorCode,
} from "../../../utils";
import { GradientCircularProgress } from "../../../../../../components/Loader";
import { MIN_REPLIES_FOR_AI_GENERATION } from "../../../constants";

import "./style.scss";

export interface CategoryPageProps {
  category: CategoryNavItem;
  onHomeClick: () => void;
  rawData: DtoCubeDataResponse;
  currentTestId: string;
  selectedFilters: SelectedFilters;
  selectedReflectionType: ReflectionType;
  setSelectedReflectionType: (type: ReflectionType) => void;
}

export const CategoryPage = ({
  category,
  onHomeClick,
  rawData,
  currentTestId,
  selectedFilters,
  selectedReflectionType,
  setSelectedReflectionType,
}: CategoryPageProps) => {
  const { t } = useTranslation();
  const [activeTabCategory, setActiveTabCategory] = useState<string>("S1");
  const [selectedCubeSide, setSelectedCubeSide] = useState<CubeSideName>("1");
  const [hoveredCubeSide, setHoveredCubeSide] = useState<CubeSideName>();
  const [isAiSummaryLoading, setIsAiSummaryLoading] = useState(false);
  const [aiSuggestionData, setAiSuggestionData] =
    useState<GenericObject<AiResponse> | null>(null);

  const cubeSideData = useReflectionCategoryPageData({
    category: category as Category,
    rawData,
    currentTestId,
    selectedFilters,
  });

  const currentSideData = cubeSideData[selectedCubeSide];
  const { reflectionTypeDistribution, summaryTabs } = currentSideData;

  const sidesSaturation = calculatePercentagesForNumbers(
    Object.fromEntries(
      Object.entries(cubeSideData).map(([key, data]) => [
        key,
        data?.cubeValues?.[selectedReflectionType],
      ])
    )
  );

  useEffect(() => {
    const aiSummaryRequestData =
      currentSideData.aiSummaryRequestData[selectedReflectionType];

    const fetchSuggestion = async () => {
      setIsAiSummaryLoading(true);
      const suggestionData = await getAnswerSuggestionForReflectionModule(
        aiSummaryRequestData
      );
      setAiSuggestionData(suggestionData.summaryData);
      setIsAiSummaryLoading(false);
    };
    if (
      aiSummaryRequestData.testSetId &&
      aiSummaryRequestData.entriesIds.length >= MIN_REPLIES_FOR_AI_GENERATION
    ) {
      fetchSuggestion();
    } else {
      setAiSuggestionData(null);
    }
  }, [selectedCubeSide, selectedReflectionType, selectedFilters]); //eslint-disable-line react-hooks/exhaustive-deps

  const renderCategoryInfo = (cubeSide: CubeSideName) => {
    const sideData = cubeSideData[cubeSide];
    const percent = sideData?.cubeValues?.[selectedReflectionType] ?? 0;
    const title = sideData.title;
    const isActive =
      selectedCubeSide === cubeSide || hoveredCubeSide === cubeSide;

    return (
      <div
        className={`side-score ${isActive ? "active" : ""}`}
        onClick={() => setSelectedCubeSide(cubeSide)}
      >
        <div className="title">{title}</div>
        <div className="value">{renderNumber(percent, false)}%</div>
      </div>
    );
  };

  const renderAiSuggestion = () => {
    if (isAiSummaryLoading) {
      return (
        <div className="summary-tabs-loader">
          <GradientCircularProgress />
        </div>
      );
    }

    if (aiSuggestionData) {
      const aiSuggestionDataCategory = aiSuggestionData[activeTabCategory];
      if (aiSuggestionDataCategory?.error) {
        return (
          <div className="tab-content">
            {translateErrorCode(aiSuggestionDataCategory?.error.code ?? "", t)}
          </div>
        );
      } else {
        return (
          <div
            className="tab-content"
            dangerouslySetInnerHTML={{
              __html: formatAiHtmlString(
                aiSuggestionData[activeTabCategory]?.summary ?? ""
              ),
            }}
          ></div>
        );
      }
    } else {
      return (
        <div className="tab-content">
          {t("frontend.futurebilityCockpit.notEnoughData")}
        </div>
      );
    }
  };

  return (
    <div className="reflection-category-page">
      <div className="left">
        <div className="category-view">
          <div className="cube-category-row">
            <MenuButton
              variant="arrow-left"
              text={
                selectedCubeSide
                  ? t("frontend.futurebilityCockpit.back", "Back")
                  : t("frontend.futurebilityCockpit.home", "Home")
              }
              onClick={onHomeClick}
            />
            <div className="cube-category-title">
              {CATEGORY_ICONS[category]}{" "}
              {t(`frontend.futurebilityCockpit.categories.${category}.title`)}
            </div>
          </div>
          <div className="extended-cube-container">
            <div className="row justify-center">{renderCategoryInfo("2")}</div>
            <div className="row justify-center">
              <div className="side-group left">
                {renderCategoryInfo("5")}
                {renderCategoryInfo("4")}
              </div>
              <div className="cube-wrapper">
                <Cube
                  greyOutOtherSidesOnHover
                  values={{ 1: 100, 2: 100, 3: 100, 4: 100, 5: 100, 6: 100 }}
                  sidesSaturation={sidesSaturation}
                  selectedSides={[selectedCubeSide]}
                  onSideHover={side => {
                    setHoveredCubeSide(side);
                  }}
                  onSideClick={setSelectedCubeSide}
                />
              </div>
              <div className="side-group right">
                {renderCategoryInfo("6")}
                {renderCategoryInfo("3")}
              </div>
            </div>
            <div className="row justify-center">{renderCategoryInfo("1")}</div>
          </div>
          <section className="cube-toggle">
            <DoubleToggle
              leftOption={{
                id: ReflectionType.Leverage,
                label: t(
                  "frontend.futurebilityCockpit.reflection.leverage",
                  "Leverage"
                ),
              }}
              rightOption={{
                id: ReflectionType.Challenge,
                label: t(
                  "frontend.futurebilityCockpit.reflection.challenges",
                  "Challenges"
                ),
              }}
              selectedOptionId={selectedReflectionType}
              onChange={optionId => {
                setSelectedReflectionType(optionId as ReflectionType);
              }}
            />
          </section>
        </div>
      </div>
      <div className="right">
        <section className="bar-chart-section">
          <BarChart
            bars={[
              {
                value: reflectionTypeDistribution[ReflectionType.Challenge],
                label: t(
                  "frontend.futurebilityCockpit.reflection.barChart.challenge",
                  "Challenge"
                ),
              },
              {
                value: reflectionTypeDistribution.none,
                label: "",
              },
              {
                value: reflectionTypeDistribution[ReflectionType.Leverage],
                label: t(
                  "frontend.futurebilityCockpit.reflection.barChart.leverage",
                  "Leverage"
                ),
              },
            ]}
            highlightBarIndex={
              selectedReflectionType === ReflectionType.Challenge
                ? 0
                : selectedReflectionType === ReflectionType.Leverage
                ? 2
                : undefined
            }
          />
        </section>
        <section>
          <div className="summary-tabs">
            <div className="tab-titles">
              {summaryTabs[selectedReflectionType].map(tab => (
                <div
                  key={tab.category}
                  className={`tab-title ${
                    tab.category === activeTabCategory ? "active" : ""
                  }`}
                  onClick={() => setActiveTabCategory(tab.category)}
                >
                  {tab.title}
                </div>
              ))}
            </div>
            {renderAiSuggestion()}
          </div>
        </section>
      </div>
    </div>
  );
};
